/**
 * Element factories — the only place this library constructs editor elements.
 *
 * Property names are the iVisora editor's own vocabulary, taken from the
 * renderers that consume them (`PageThumbnailPreview`, `staticTextRender`,
 * `frameLayout`), not invented. Because every element is built here, every
 * element carries the full required field set — so nothing a template produces
 * can be malformed, and everything it produces is fully editable in the editor:
 * text types, shapes recolour, images swap, frames accept a drop.
 */

import { applyCase } from './typography';

/**
 * The editor's static text block renders inside `padding: 0 8px`
 * (see staticTextRender.jsx StaticTextBlock). Text therefore starts 8px inside
 * its element box. Every text factory widens the box by 8px on each side and
 * shifts it left by 8px, so the CONTENT edge lands exactly on the x the
 * template asked for — and left, centre and right alignment all stay true
 * because the compensation is symmetric.
 */
export const TEXT_INSET = 8;

const round = (n) => Math.round(n);

const base = (id, x, y, width, height, rotation = 0) => ({
  id,
  x: round(x),
  y: round(y),
  width: round(width),
  height: round(height),
  rotation,
  opacity: 1,
  locked: false,
  visible: true,
  parentId: null,
});

/** Line count of authored copy. Templates control breaks with explicit \n. */
export const countLines = (copy) => String(copy).split('\n').length;

/** Rendered height of a text block, from its own type spec. */
export const textHeight = (copy, { fontSize, lineHeight }) =>
  Math.ceil(fontSize * lineHeight * countLines(copy));

/**
 * Text element.
 *
 * @param id      stable element id
 * @param role    a spec from `typography.roles()` — family/size/weight/leading
 * @param copy    the content; `\n` forces a line break
 * @param box     { x, y, width, align } — x/width describe the TEXT column,
 *                not the element box (see TEXT_INSET)
 * @param over    optional overrides (color, fontSize, opacity, rotation, …)
 */
export const text = (id, role, copy, { x, y, width, align = 'left' }, over = {}) => {
  const spec = { ...role, ...over };
  const content = applyCase(role, copy);
  const height = over.height ?? textHeight(content, spec);
  return {
    ...base(id, x - TEXT_INSET, y, width + TEXT_INSET * 2, height, over.rotation ?? 0),
    type: 'text',
    text: content,
    fontFamily: spec.fontFamily,
    fontSize: spec.fontSize,
    fontWeight: spec.fontWeight,
    fontStyle: over.fontStyle ?? 'normal',
    color: over.color ?? '#000000',
    textAlign: align,
    letterSpacing: `${spec.letterSpacing ?? 0}px`,
    lineHeight: spec.lineHeight,
    ...(over.opacity !== undefined ? { opacity: over.opacity } : {}),
    // Script faces carry ascenders and descenders far outside their line box,
    // so a signature word is MEANT to tuck over the headline beneath it — the
    // boxes overlap while the glyphs do not. `overlapOk` is the explicit,
    // per-element opt-out from the collision gate; nothing else may skip it.
    ...(over.overlapOk || spec.isScript
      ? { meta: { ...(over.overlapOk ? { overlapOk: true } : {}), ...(spec.isScript ? { script: true } : {}) } }
      : {}),
    ...(over.shadow
      ? {
          shadowOffsetX: over.shadow.offsetX,
          shadowOffsetY: over.shadow.offsetY,
          shadowBlur: over.shadow.blur,
          shadowColor: over.shadow.color,
        }
      : {}),
    ...(over.gradient ? { fillGradient: over.gradient } : {}),
  };
};

/**
 * Shape element. `kind` maps to the editor's shape switch:
 * rect (default) | roundedRect | circle | triangle | diamond | hexagon |
 * pentagon | octagon | star | heart | arrow | cross | parallelogram.
 */
export const shape = (id, { x, y, width, height, kind = 'rect', fill, fillOpacity, cornerRadius = 0, stroke, strokeWidth = 0, gradient, rotation = 0, opacity }) => ({
  ...base(id, x, y, width, height, rotation),
  type: 'shape',
  shape: kind,
  fill: fill ?? '#000000',
  stroke: stroke ?? 'none',
  strokeWidth,
  cornerRadius,
  ...(fillOpacity !== undefined ? { fillOpacity } : {}),
  ...(gradient ? { fillGradient: gradient } : {}),
  ...(opacity !== undefined ? { opacity } : {}),
});

/** Hairline / rule. A shape, but the intent is legible at the call site. */
export const rule = (id, { x, y, width, weight, color, opacity }) =>
  shape(id, { x, y, width, height: weight, kind: 'rect', fill: color, opacity });

/**
 * Photograph. `src` must be an https URL (the app's CSP allows `img-src https:`)
 * and is rendered cover-fit, so any replacement image the user drops in keeps
 * the same crop behaviour.
 */
export const image = (id, { x, y, width, height, src, cornerRadius, cornerRadii, rotation = 0, opacity }) => ({
  ...base(id, x, y, width, height, rotation),
  type: 'image',
  src,
  fit: 'cover',
  ...(cornerRadius ? { cornerRadius } : {}),
  ...(cornerRadii ? { cornerRadii } : {}),
  ...(opacity !== undefined ? { opacity } : {}),
});

/**
 * Empty photo frame — the editor's native image drop-zone, in a cut shape.
 * `shapeId` comes from frameShapes.js: rectangle | rounded | circle |
 * triangle | pentagon | hexagon | heart | arch.
 */
export const frame = (id, { x, y, width, height, shapeId = 'rectangle', rotation = 0 }) => ({
  ...base(id, x, y, width, height, rotation),
  type: 'frame',
  shape: shapeId,
  fit: 'cover',
  image: null,
});

/** Emoji sticker — the editor's icon primitive. */
export const sticker = (id, { x, y, size, emoji, rotation = 0 }) => ({
  ...base(id, x, y, size, size, rotation),
  type: 'sticker',
  emoji,
  fontSize: Math.round(size * 0.8),
});

/**
 * Inline SVG icon. `content` carries the editor's `{color}` template, which
 * `processSvgForRender` substitutes with `fill` — so the icon stays a live,
 * re-colourable element in the editor instead of a flattened graphic.
 */
export const svgIcon = (id, { x, y, width, height, content, fill, rotation = 0, opacity }) => ({
  ...base(id, x, y, width, height ?? width, rotation),
  type: 'svg',
  svgContent: content,
  colors: [],
  colorMap: {},
  fill,
  ...(opacity !== undefined ? { opacity } : {}),
});

/** Native QR element — scannable in the editor, the preview and the export. */
export const qrcode = (id, { x, y, size, data, color = '#000000', bgColor = '#ffffff' }) => ({
  ...base(id, x, y, size, size),
  type: 'qrcode',
  data,
  color,
  bgColor,
  design: 'square',
});

/**
 * Cut a photograph to a silhouette (circle, hexagon, arch, …).
 *
 * The editor clips by pairing an invisible mask shape (`isMaskShape`) with
 * content that points at it via `clippedTo`. Its clip support assumes the two
 * OCCUPY THE SAME BOX at the SAME ROTATION — that is how the UI creates a mask
 * (draw a shape over an image, then mask), and how the only in-repo examples
 * are written (utils/exportRegressionScenes.js). A mask placed elsewhere, or
 * turned to a different angle, does not resolve, and an unresolved
 * `clip-path: url()` does not degrade — it removes the element from the page
 * entirely. So the box is taken from the content and cannot be given
 * separately; the shape is the only thing a caller chooses.
 *
 * For an angled EDGE between a photograph and a colour field, use
 * `angledField` instead: a rotated opaque panel in the page colour reads the
 * same and does not depend on cross-element references.
 *
 * @param maskId  id for the generated mask shape
 * @param content an element built by `image`
 * @param kind    an editor shape id — circle | hexagon | star | triangle | …
 */
export const clipped = (maskId, content, kind = 'circle') => {
  const mask = {
    ...base(maskId, content.x, content.y, content.width, content.height, content.rotation || 0),
    type: 'shape',
    shape: kind,
    fill: 'none',
    stroke: 'none',
    strokeWidth: 0,
    cornerRadius: 0,
    isMaskShape: true,
    maskChildIds: [content.id],
  };
  return [mask, { ...content, clippedTo: maskId }];
};

/** Assemble a page. `background` accepts a hex or any CSS background string. */
export const page = (id, { name = 'Page 1', background, elements }) => ({
  id,
  name,
  background,
  visible: true,
  locked: false,
  elements,
});
